/**
 * Participant Capacity Calculator
 *
 * Computes per-participant available hours within a schedule window,
 * based on the participant's AvailabilityWindow ranges.
 *
 * Recurring weekday unavailability rules are expected to be pre-materialized
 * into the availability windows before reaching this module (see
 * config-store's computeAvailability()). This avoids double-subtraction.
 */

import type { Participant, ParticipantCapacity, Task } from '../models/types';
import { operationalDateKey } from './date-utils';

/**
 * Sanctioned bounds for the per-participant fair-share `workloadMultiplier`.
 *
 * The multiplier DIVIDES effective capacity (`effectiveCapacity` in
 * soft-constraints.ts) and the optimizer/rescue greedy load-proximity scores,
 * so it directly scales the participant's SC-3/SC-8 fairness target. An
 * out-of-range value silently breaks that target: a huge multiplier (e.g. a
 * tampered `1e308`) collapses the target toward 0 (the person is excluded from
 * soft balancing); a tiny one inflates it (the person becomes a load magnet).
 *
 * Floor `0.1` preserves every value the UI can legitimately produce on the low
 * end (the participant-editor stepper floors at 0.1); ceiling `5` is the
 * established app maximum (the XLSX importer and the table editor both cap at
 * 5). The stricter `0.3` low-end *guidance* used by those two surfaces is a
 * subset of this safety range, not in conflict with it.
 */
export const WORKLOAD_MULTIPLIER_MIN = 0.1;
export const WORKLOAD_MULTIPLIER_MAX = 5;

/**
 * Clamp a participant's `workloadMultiplier` to `[WORKLOAD_MULTIPLIER_MIN,
 * WORKLOAD_MULTIPLIER_MAX]`, mapping the neutral / garbage cases (undefined,
 * NaN, ±Infinity, ≤ 0) to the default `1`.
 *
 * This is the SINGLE canonical guard, applied BOTH at the JSON import
 * boundaries (`config-store` loadFromStorage / loadParticipantSet /
 * _normalizeParticipantSet — none of which went through the UI/XLSX clamps, so
 * a tampered full-backup or `.gm.json` could smuggle an out-of-range value)
 * AND at the engine read chokepoints (`effectiveCapacity`, the optimizer
 * `workloadScore`, and rescue `sortParticipantsByLoadProximity`). Mirrors the
 * rest-rule / template-numeric double-guard model documented in CLAUDE.md.
 *
 * It is **byte-identical to the legacy `p.workloadMultiplier ?? 1` and
 * `m > 0 ? … : 1` fallbacks for every in-range value**, so non-tampered data is
 * never perturbed — only the pathological extremes are bounded.
 */
export function clampWorkloadMultiplier(m: number | undefined | null): number {
  if (m == null || !Number.isFinite(m) || m <= 0) return 1;
  return Math.max(WORKLOAD_MULTIPLIER_MIN, Math.min(WORKLOAD_MULTIPLIER_MAX, m));
}

/**
 * Compute available hours for a single operational day, given a participant's
 * availability windows.
 *
 * @param opDayStart  The start of the operational day (dayStartHour on some calendar date)
 * @param participant The participant with materialized availability windows
 * @returns Available hours on that operational day (0–24)
 */
function computeDayAvailableHours(opDayStart: Date, participant: Participant): number {
  const dayStartMs = opDayStart.getTime();
  const dayEndMs = dayStartMs + 24 * 3_600_000; // exactly 24 hours

  // 1. Compute hours covered by availability windows on this day
  let availableHours = 0;
  for (const w of participant.availability) {
    const overlapStart = Math.max(dayStartMs, w.start.getTime());
    const overlapEnd = Math.min(dayEndMs, w.end.getTime());
    if (overlapEnd > overlapStart) {
      availableHours += (overlapEnd - overlapStart) / 3_600_000;
    }
  }

  if (availableHours <= 0) return 0;

  // NOTE: Recurring weekday unavailability rules are NOT re-applied here.
  // In the web app, participant.availability already has unavailability
  // holes carved out by config-store's computeAvailability(). Re-subtracting
  // dateUnavailability would double-count, underestimating capacity.
  // Library consumers constructing Participant objects manually should
  // pre-process dateUnavailability into their availability windows.

  return availableHours;
}

/**
 * Compute the capacity for a single participant within a schedule window.
 *
 * @param participant    The participant
 * @param scheduleStart  Start of the schedule window (inclusive)
 * @param scheduleEnd    End of the schedule window (exclusive)
 * @param dayStartHour   The operational day boundary hour (0–23)
 * @returns ParticipantCapacity with totalAvailableHours and dailyAvailableHours
 */
export function computeParticipantCapacity(
  participant: Participant,
  scheduleStart: Date,
  scheduleEnd: Date,
  dayStartHour: number = 5,
): ParticipantCapacity {
  const dailyAvailableHours = new Map<string, number>();
  let totalAvailableHours = 0;

  // Iterate over each operational day in the schedule window.
  // An operational day starts at dayStartHour on a calendar date and runs 24 hours.
  // If scheduleStart falls before today's dayStartHour (e.g. a task stamped at
  // 05:00 on day 1 with dayStartHour=6), that timestamp belongs to the previous
  // calendar day's operational period — the cursor must start there so the map
  // key aligns with operationalDateKey() for such tasks.
  const cursor = new Date(
    scheduleStart.getFullYear(),
    scheduleStart.getMonth(),
    scheduleStart.getDate(),
    dayStartHour,
    0,
    0,
    0,
  );
  if (cursor.getTime() > scheduleStart.getTime()) {
    cursor.setDate(cursor.getDate() - 1);
  }
  const endMs = scheduleEnd.getTime();

  while (cursor.getTime() < endMs) {
    const hours = computeDayAvailableHours(cursor, participant);
    const dk = operationalDateKey(cursor, dayStartHour);
    dailyAvailableHours.set(dk, hours);
    totalAvailableHours += hours;

    // Advance to next operational day (24 hours)
    cursor.setTime(cursor.getTime() + 24 * 3_600_000);
  }

  return { totalAvailableHours, dailyAvailableHours };
}

/**
 * Compute capacities for all participants within a schedule window.
 *
 * @param participants   All participants
 * @param scheduleStart  Start of the schedule window
 * @param scheduleEnd    End of the schedule window
 * @param dayStartHour   The operational day boundary hour (0–23)
 * @returns Map from participant ID to ParticipantCapacity
 */
export function computeAllCapacities(
  participants: Participant[],
  scheduleStart: Date,
  scheduleEnd: Date,
  dayStartHour: number = 5,
): Map<string, ParticipantCapacity> {
  const result = new Map<string, ParticipantCapacity>();
  for (const p of participants) {
    result.set(p.id, computeParticipantCapacity(p, scheduleStart, scheduleEnd, dayStartHour));
  }
  return result;
}

/**
 * Engine capacity window: the minimal span covering every task's time block,
 * `[min(task.timeBlock.start), max(task.timeBlock.end)]`. This is the
 * demand-proportional window the engine feeds to `computeAllCapacities` for
 * capacity-proportional SC-3/SC-8 targets — a day with no work needs no
 * reserved capacity, so truncating to the occupied task span is correct.
 *
 * ⚠️ Deliberately DISTINCT from the display-side `getCapacityWindow`
 * (`web/workload-utils.ts`), which spans the WHOLE operational period so
 * "% of availability" headlines aren't inflated by a gappy/short-tailed
 * schedule. Do not unify the two — see the "Capacity window" design rule in
 * CLAUDE.md. Every engine task-span site (scheduler `_buildScoreCtx`,
 * optimizer, `capability-change.buildLocalCapacities`, auto-tuner `freezeEnv`)
 * routes through this helper.
 *
 * Empty `tasks` ⇒ a degenerate current-time window (`start`/`end` ≈ now),
 * preserving the legacy `tasks[0]?.timeBlock.start ?? new Date()` fallback at
 * the engine call sites; such a window yields ~zero capacity and never arises
 * in practice (the engine never scores an empty task set). Callers that need
 * an empty result for empty input (e.g. `buildLocalCapacities`) guard before
 * calling.
 */
export function taskSpanWindow(tasks: Task[]): { start: Date; end: Date } {
  let start = tasks[0]?.timeBlock.start ?? new Date();
  let end = tasks[0]?.timeBlock.end ?? new Date();
  for (const t of tasks) {
    if (t.timeBlock.start < start) start = t.timeBlock.start;
    if (t.timeBlock.end > end) end = t.timeBlock.end;
  }
  return { start, end };
}

/**
 * Build the four shared score-context building blocks: the task/participant
 * lookup maps, capacity-proportional per-participant capacities over the task
 * span (`taskSpanWindow`), and the "not with" pair index.
 *
 * Both the engine's per-schedule `ScoreContext` (`scheduler._buildScoreCtx`)
 * and the auto-tuner's frozen world (`auto-tuner.freezeEnv`) are assembled from
 * this same primitive — `freezeEnv` cannot call `_buildScoreCtx` (a private
 * engine method that runs against an instantiated engine, which does not yet
 * exist when the world is frozen once for the worker pool). Each caller spreads
 * this result and adds its own day-start key (`dayStartHour` vs `dayStart`) plus
 * any extra fields. Kept in this module because it is Node-safe (no web-layer
 * deps) — a requirement of `freezeEnv`'s build graph — and both consumers
 * already import `computeAllCapacities`/`taskSpanWindow` from here.
 */
export function buildScoreContextFields(
  tasks: Task[],
  participants: Participant[],
  dayStartHour: number,
): {
  taskMap: Map<string, Task>;
  pMap: Map<string, Participant>;
  capacities: Map<string, ParticipantCapacity>;
  notWithPairs: Map<string, Set<string>>;
} {
  const { start, end } = taskSpanWindow(tasks);
  const notWithPairs = new Map<string, Set<string>>();
  for (const p of participants) {
    if (p.notWithIds && p.notWithIds.length > 0) {
      notWithPairs.set(p.id, new Set(p.notWithIds));
    }
  }
  return {
    taskMap: new Map(tasks.map((t) => [t.id, t])),
    pMap: new Map(participants.map((p) => [p.id, p])),
    capacities: computeAllCapacities(participants, start, end, dayStartHour),
    notWithPairs,
  };
}
